import React, { useState, useEffect, useMemo, useRef } from "react";
import "./viewImages.scss";
import {
  compressImage,
  getFileInfo,
  getFileIcon,
} from "../../../../utils/helper";
import Loader from "../../../../library/common/Loader";
import ImageModal from "../../../../library/custom/imageModal/imageModal";

const ViewImages = ({
  imageSize,
  image,
  singleImage = false,
  singleImagespath,
  multiImage = true,
  singleImagesid,
  showName = true,
}) => {
  const [clickedImg, setClickedImg] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [compressedImages, setCompressedImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const previousImagesRef = useRef(null);

  const handleClick = async (imageVal, i) => {
    try {
      setCurrentIndex(i);
      setClickedImg(imageVal);
    } catch (error) {
      console.error("Error handling click:", error);
    }
  };

  const handleRotationRight = () => {
    if (compressedImages.length === 0 || currentIndex === null) return;

    const newIndex = (currentIndex + 1) % compressedImages.length;
    setCurrentIndex(newIndex);
    setClickedImg(originalImages[newIndex]);
  };

  const handleRotationLeft = () => {
    if (compressedImages.length === 0 || currentIndex === null) return;

    const newIndex =
      (currentIndex - 1 + compressedImages.length) % compressedImages.length;
    setCurrentIndex(newIndex);
    setClickedImg(originalImages[newIndex]);
  };

  // Only real images go through the compress/decode pipeline. PDFs/docs
  // fail image compression and were previously dropped from the list entirely.
  const imageUrls = useMemo(() => {
    if (!multiImage || !image || !Array.isArray(image)) return [];
    return image.filter((url) => getFileInfo(url)?.isImage);
  }, [image, multiImage]);

  const otherFiles = useMemo(() => {
    if (!multiImage || !image || !Array.isArray(image)) return [];
    return image
      .filter((url) => typeof url === "string" && url.trim())
      .filter((url) => !getFileInfo(url)?.isImage)
      .map((url) => ({ url, ...getFileInfo(url) }));
  }, [image, multiImage]);

  // Create a stable string representation of the images array to detect actual changes
  const imageKey = useMemo(() => imageUrls.join("|"), [imageUrls]);

  useEffect(() => {
    const compressAllImages = async () => {
      try {
        // Only compress if images actually changed
        if (previousImagesRef.current === imageKey) {
          return;
        }

        setLoading(true);
        const compressed = await Promise.all(
          imageUrls.map(async (img) => await compressImage(img)),
        );

        const validCompressedImages = compressed.filter((url) => url !== null);
        setCompressedImages(validCompressedImages);
        setOriginalImages(imageUrls);
        previousImagesRef.current = imageKey;
        setLoading(false);
      } catch (error) {
        console.error("Error compressing images:", error);
        setLoading(false);
      }
    };

    if (multiImage && imageUrls.length > 0) {
      compressAllImages();
    } else {
      // Reset states when no images
      setCompressedImages([]);
      setOriginalImages([]);
      previousImagesRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageKey, multiImage]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : multiImage &&
        (compressedImages.length > 0 || otherFiles.length > 0) ? (
        <div className="viewImageBox">
          <div
            className={
              imageSize?.size === "small" ? "viewList small" : "viewList large"
            }
          >
            {compressedImages.map((imageVal, i) => {
              const imageNameS = originalImages[i]?.split("/");
              const imageName =
                imageNameS?.length > 0 && imageNameS.slice(-1).pop();
              return (
                <div className="listItem" key={i}>
                  {imageVal && (
                    <>
                      <img
                        onClick={() => handleClick(originalImages[i], i)}
                        src={imageVal}
                        alt={imageName}
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                      />
                      {showName && <div className="imageName">{imageName}</div>}
                    </>
                  )}
                </div>
              );
            })}

            {otherFiles.map((file, i) => (
              <div className="listItem" key={`${file.url}-${i}`}>
                <img
                  src={getFileIcon(file.url)}
                  alt={file.fileName}
                  onClick={() =>
                    window.open(file.url, "_blank", "noopener,noreferrer")
                  }
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    cursor: "pointer",
                  }}
                />
                {showName && <div className="imageName">{file.fileName}</div>}
              </div>
            ))}

            {clickedImg && (
              <ImageModal
                clickedImg={clickedImg}
                handleRotationRight={handleRotationRight}
                setClickedImg={setClickedImg}
                handleRotationLeft={handleRotationLeft}
              />
            )}
          </div>
        </div>
      ) : null}
      {singleImage && (
        <div className="viewImageBox">
          <div
            className={
              imageSize?.size === "small" ? "viewList small" : "viewList large"
            }
          >
            <div className="listItem">
              <img src={singleImagespath} alt="" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewImages;
